// src/components/Admin/Statistics.js
import React, { useState, useEffect } from 'react';
import { Row, Col, Card, Spinner, Alert } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import {
  FaUsers, FaImage, FaComment, FaHeart,
  FaCamera, FaCommentDots, FaMedal, FaExclamationTriangle,
  FaRedoAlt
} from 'react-icons/fa';
import { useUserAuth } from '../../context/UserAuthContext';
import api from '../../services/api';
import './Admin.css';

const Statistics = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const { user } = useUserAuth();
  const { t } = useTranslation();

  const fetchStatistics = async () => {
    try {
      setLoading(true);
      setError('');

      const response = await api.get('/admin/statistics');

      setStats(response.data.statistics || {});

    } catch (err) {
      console.error('Error fetching statistics:', err);
      setError('Failed to load statistics');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStatistics();
  }, []);

  if (loading) {
    return (
        <div className="admin-loading-container">
          <Spinner animation="border" role="status" className="admin-loading-spinner">
            <span className="visually-hidden">{t('loading')}</span>
          </Spinner>
          <p className="admin-loading-text">{t('adminDashboard.loadingStats')}</p>
        </div>
    );
  }

  if (error) {
    return (
        <div className="admin-error-state">
          <div className="admin-error-icon">
            <FaExclamationTriangle />
          </div>
          <h3 className="admin-error-title">{t('adminDashboard.failedToLoad')}</h3>
          <p className="admin-error-message">{error}</p>
          <button className="admin-retry-button" onClick={fetchStatistics}>
            <FaRedoAlt className="admin-retry-icon" />
            {t('adminDashboard.tryAgain')}
          </button>
        </div>
    );
  }

  return (
      <div className="admin-statistics">
        <h2 className="admin-section-title">{t('adminDashboard.dashboardOverview')}</h2>

        {/* Summary Cards */}
        <Row xs={1} md={2} lg={4} className="g-4 mb-4">
          <Col>
            <Card className="summary-card users-card fade-in delay-1">
              <Card.Body>
                <div className="summary-icon">
                  <FaUsers />
                </div>
                <div className="summary-content">
                  <h3>{stats?.counts?.users || 0}</h3>
                  <p>{t('adminDashboard.totalUsers')}</p>
                </div>
              </Card.Body>
            </Card>
          </Col>

          <Col>
            <Card className="summary-card photos-card fade-in delay-2">
              <Card.Body>
                <div className="summary-icon">
                  <FaImage />
                </div>
                <div className="summary-content">
                  <h3>{stats?.counts?.photos || 0}</h3>
                  <p>{t('adminDashboard.totalPhotos')}</p>
                </div>
              </Card.Body>
            </Card>
          </Col>

          <Col>
            <Card className="summary-card wishes-card fade-in delay-3">
              <Card.Body>
                <div className="summary-icon">
                  <FaComment />
                </div>
                <div className="summary-content">
                  <h3>{stats?.counts?.wishes || 0}</h3>
                  <p>{t('adminDashboard.totalWishes')}</p>
                </div>
              </Card.Body>
            </Card>
          </Col>

          <Col>
            <Card className="summary-card likes-card fade-in delay-3">
              <Card.Body>
                <div className="summary-icon">
                  <FaHeart />
                </div>
                <div className="summary-content">
                  <h3>{stats?.counts?.likes || 0}</h3>
                  <p>{t('adminDashboard.totalLikes')}</p>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Recent Activity and Top Photos */}
        <Row className="g-4">
          <Col lg={6}>
            <Card className="admin-card h-100 fade-in delay-2">
              <Card.Header className="admin-card-header">
                <div className="admin-card-header-icon">
                  <FaCamera />
                </div>
                <h3 className="admin-card-title">{t('adminDashboard.recentPhotos')}</h3>
              </Card.Header>
              <Card.Body className="admin-card-body">
                {stats?.recentActivity?.photos?.length === 0 ? (
                    <div className="admin-empty-state">
                      <div className="admin-empty-icon">
                        <FaCamera />
                      </div>
                      <p className="admin-empty-text">{t('adminDashboard.noPhotosYet')}</p>
                    </div>
                ) : (
                    <div className="admin-activity-list">
                      {stats?.recentActivity?.photos?.map(photo => (
                          <div key={photo.id} className="admin-activity-item">
                            <div className="admin-activity-image">
                              <img
                                  src={photo.imageUrl}
                                  alt={photo.description || t('photoGallery.photoBy', { name: photo.userName })}
                                  className="admin-thumbnail"
                              />
                            </div>
                            <div className="admin-activity-details">
                              <p className="admin-activity-user">{photo.userName}</p>
                              <p className="admin-activity-description">
                                {photo.description || t('adminDashboard.noDescription')}
                              </p>
                              <p className="admin-activity-date">
                                {new Date(photo.uploadedAt).toLocaleDateString()}
                              </p>
                            </div>
                          </div>
                      ))}
                    </div>
                )}
              </Card.Body>
            </Card>
          </Col>

          <Col lg={6}>
            <Card className="admin-card h-100 fade-in delay-3">
              <Card.Header className="admin-card-header">
                <div className="admin-card-header-icon">
                  <FaCommentDots />
                </div>
                <h3 className="admin-card-title">{t('adminDashboard.recentWishes')}</h3>
              </Card.Header>
              <Card.Body className="admin-card-body">
                {stats?.recentActivity?.wishes?.length === 0 ? (
                    <div className="admin-empty-state">
                      <div className="admin-empty-icon">
                        <FaCommentDots />
                      </div>
                      <p className="admin-empty-text">{t('adminDashboard.noWishesYet')}</p>
                    </div>
                ) : (
                    <div className="admin-activity-list">
                      {stats?.recentActivity?.wishes?.map(wish => (
                          <div key={wish.id} className="admin-activity-item">
                            <div className="admin-activity-details">
                              <p className="admin-activity-user">{wish.userName}</p>
                              <p className="admin-activity-description">
                                {wish.message}
                              </p>
                              <p className="admin-activity-date">
                                {new Date(wish.createdAt).toLocaleDateString()}
                              </p>
                            </div>
                          </div>
                      ))}
                    </div>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Top Photos */}
        <Card className="admin-card mt-4 fade-in delay-4">
          <Card.Header className="admin-card-header">
            <div className="admin-card-header-icon">
              <FaMedal />
            </div>
            <h3 className="admin-card-title">{t('adminDashboard.topPhotos')}</h3>
          </Card.Header>
          <Card.Body className="admin-card-body">
            {stats?.topPhotos?.length === 0 ? (
                <div className="admin-empty-state">
                  <div className="admin-empty-icon">
                    <FaImage />
                  </div>
                  <p className="admin-empty-text">{t('adminDashboard.noPhotosYet')}</p>
                </div>
            ) : (
                <div className="admin-photos-grid">
                  {stats?.topPhotos?.map((photo, index) => (
                      <div key={photo.id} className="admin-photo-card">
                        <div className="admin-photo-rank">#{index + 1}</div>
                        <div className="admin-photo-image">
                          <img
                              src={photo.imageUrl}
                              alt={photo.description || t('photoGallery.photoBy', { name: photo.userName })}
                          />
                        </div>
                        <div className="admin-photo-details">
                          <p className="admin-photo-user">{photo.userName}</p>
                          <p className="admin-photo-likes">
                            <FaHeart className="admin-like-icon" /> {photo.likeCount || 0}
                          </p>
                        </div>
                      </div>
                  ))}
                </div>
            )}
          </Card.Body>
        </Card>
      </div>
  );
};

export default Statistics;